import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@awj/api-client';

import { admin } from './queries.ts';

type Moderate = components['schemas']['ModerateReviewRequest'];

export interface ReviewQuery {
  status?: string;
  page?: number;
  pageSize?: number;
}

export function useReviews(query: ReviewQuery) {
  return useQuery({
    queryKey: ['reviews', 'list', query],
    queryFn: () =>
      admin().reviews({
        status: query.status || undefined,
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 20,
      }),
  });
}

/**
 * Moderating and replying.
 *
 * Both drop the whole reviews section rather than patching one row: approving a review changes
 * the product's stored rating, and the pending count in the sidebar and on the dashboard moves
 * with it. A surgical update would leave two of those three stale.
 */
function useReviewInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: ['reviews'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['products'] });
  };
}

export function useModerateReview() {
  const invalidate = useReviewInvalidation();

  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Moderate }) => admin().moderateReview(id, body),
    onSuccess: invalidate,
  });
}

export function useReplyToReview() {
  const invalidate = useReviewInvalidation();

  return useMutation({
    mutationFn: ({ id, reply }: { id: number; reply: string }) => admin().replyToReview(id, { reply }),
    onSuccess: invalidate,
  });
}

export interface InquiryQuery {
  search?: string;
  status?: string;
  type?: string;
  page?: number;
  pageSize?: number;
}

export function useInquiries(query: InquiryQuery) {
  return useQuery({
    queryKey: ['inquiries', 'list', query],
    queryFn: () =>
      admin().inquiries({
        search: query.search || undefined,
        status: query.status || undefined,
        type: query.type || undefined,
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 20,
      }),
  });
}

export function useInquiry(id: number | null) {
  return useQuery({
    queryKey: ['inquiries', 'one', id],
    queryFn: () => admin().inquiry(id!),
    enabled: id !== null,
  });
}

function useInquiryInvalidation() {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

export function useSetInquiryStatus() {
  const queryClient = useQueryClient();
  const invalidate = useInquiryInvalidation();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => admin().setInquiryStatus(id, { status }),
    onSuccess: (inquiry) => {
      queryClient.setQueryData(['inquiries', 'one', inquiry.id], inquiry);
      invalidate();
    },
  });
}

export function useAddInquiryNote() {
  const queryClient = useQueryClient();
  const invalidate = useInquiryInvalidation();

  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: string }) => admin().addInquiryNote(id, { body }),
    onSuccess: (inquiry) => {
      queryClient.setQueryData(['inquiries', 'one', inquiry.id], inquiry);
      invalidate();
    },
  });
}
